// front/src/preview-mode/graph/sessions.ts
import type { Rng } from "./seed";
import type {
  Channel,
  PageEdge,
  PageGraph,
  PreviewDevice,
  PreviewSession,
  PreviewSessionEvent,
  SessionOutcome,
} from "./types";

export const SESSION_COUNT = 2000;

// Previous-period synthesized sessions are slightly fewer than the current
// period and skew towards bounces / away from conversions so dashboard
// "compare:true" deltas show plausible growth across most KPIs.
export const PREVIOUS_SESSION_COUNT = 1740;

// Reserve headroom so a session's events cannot extend past windowEnd.
// Worst case: 8 pageviews × 60s + conversion events (~15s) ≈ 8 minutes.
// Use a generous 30-minute buffer for safety.
const MAX_SESSION_DURATION_MS = 30 * 60 * 1000;

interface GenArgs {
  devices: PreviewDevice[];
  pageGraph: PageGraph;
  windowStart: number;
  windowEnd: number;
  outcomeWeights?: ReadonlyArray<{ value: SessionOutcome; weight: number }>;
  count?: number;
}

const OUTCOME_WEIGHTS: Array<{ value: SessionOutcome; weight: number }> = [
  { value: "bounce", weight: 40 },
  { value: "engaged", weight: 50 },
  { value: "converting", weight: 10 },
];

export const PREVIOUS_OUTCOME_WEIGHTS: ReadonlyArray<{
  value: SessionOutcome;
  weight: number;
}> = [
  { value: "bounce", weight: 48 },
  { value: "engaged", weight: 46 },
  { value: "converting", weight: 6 },
];

const CHANNEL_WEIGHTS: Array<{ value: Channel; weight: number }> = [
  { value: "organic", weight: 35 },
  { value: "paid", weight: 25 },
  { value: "direct", weight: 20 },
  { value: "email", weight: 10 },
  { value: "social", weight: 10 },
];

const UTM_CAMPAIGNS: Record<Channel, string[]> = {
  organic: [],
  paid: [
    "brand-search",
    "service-line-cardiology",
    "service-line-oncology",
    "service-line-orthopedics",
    "service-line-maternity",
    "local-chicago",
    "local-indy",
  ],
  direct: [],
  email: ["newsletter-2026-q1", "appointment-reminder", "welcome-series"],
  social: ["fb-locations-retarget", "ig-providers-awareness"],
};

function pickChannel(rng: Rng): Channel {
  return rng.weightedPick(CHANNEL_WEIGHTS);
}

function utmFor(channel: Channel, rng: Rng) {
  const campaigns = UTM_CAMPAIGNS[channel];
  if (campaigns.length === 0) return undefined;
  return {
    source:
      channel === "paid"
        ? rng.pick(["google", "bing", "meta"])
        : channel === "email"
          ? "email"
          : channel === "social"
            ? rng.pick(["facebook", "instagram"])
            : channel,
    medium:
      channel === "paid"
        ? "cpc"
        : channel === "email"
          ? "email"
          : channel === "social"
            ? "social"
            : channel,
    campaign: rng.pick(campaigns),
  };
}

function pickEntryPath(
  pageGraph: PageGraph,
  channel: Channel,
  rng: Rng,
): string {
  // Weight entry points by the channel weight declared for each entry.
  const options = pageGraph.entryPoints.map((ep) => ({
    value: ep.path,
    weight: ep.channelWeights[channel] ?? 1,
  }));
  return rng.weightedPick(options);
}

function outgoing(pageGraph: PageGraph, path: string): PageEdge[] {
  return pageGraph.edges.filter((e) => e.from === path && e.weight > 0);
}

function pickStartTimestamp(
  rng: Rng,
  windowStart: number,
  windowEnd: number,
): number {
  // Rough weekday/hour weighting: sample a day uniformly then bias hours.
  const spanMs = windowEnd - windowStart;
  const days = Math.ceil(spanMs / (24 * 60 * 60 * 1000));
  const dayOffset = rng.int(days);
  const dayStart = windowStart + dayOffset * 24 * 60 * 60 * 1000;
  // Hour weights: 0–5 low, 6–8 mid, 9–16 high, 17–21 mid, 22–23 low
  const hourWeights = [
    1, 1, 1, 1, 1, 1, 3, 4, 5, 8, 9, 9, 9, 9, 8, 8, 7, 6, 5, 4, 3, 2, 1, 1,
  ].map((w, h) => ({ value: h, weight: w }));
  const hour = rng.weightedPick(hourWeights);
  const minute = rng.int(60);
  const second = rng.int(60);
  const ts = dayStart + ((hour * 60 + minute) * 60 + second) * 1000;
  const maxStart = Math.max(windowStart, windowEnd - MAX_SESSION_DURATION_MS);
  return Math.min(Math.max(ts, windowStart), maxStart);
}

function walkOnce(
  rng: Rng,
  pageGraph: PageGraph,
  startPath: string,
  maxSteps: number,
): string[] {
  const path = [startPath];
  let current = startPath;
  for (let step = 1; step < maxSteps; step++) {
    const edges = outgoing(pageGraph, current);
    if (edges.length === 0) break;
    const chosen = rng.weightedPick(
      edges.map((e) => ({ value: e, weight: e.weight })),
    );
    path.push(chosen.to);
    current = chosen.to;
    if (current === "/appointments/new") break;
  }
  return path;
}

export function generateSessions(rng: Rng, args: GenArgs): PreviewSession[] {
  const { devices, pageGraph, windowStart, windowEnd } = args;
  const outcomeWeights = args.outcomeWeights ?? OUTCOME_WEIGHTS;
  const count = args.count ?? SESSION_COUNT;
  const sessions: PreviewSession[] = [];

  for (let i = 0; i < count; i++) {
    const device = devices[rng.int(devices.length)];
    const channel = pickChannel(rng);
    const outcome = rng.weightedPick(outcomeWeights);
    const startedAt = pickStartTimestamp(rng, windowStart, windowEnd);
    const entryPath = pickEntryPath(pageGraph, channel, rng);

    let pathSteps: string[];
    if (outcome === "bounce") {
      pathSteps = [entryPath];
    } else if (outcome === "engaged") {
      pathSteps = walkOnce(rng, pageGraph, entryPath, 3 + rng.int(6)); // 3–8 pages
    } else {
      // converting: force a walk that ends near appointments/new
      pathSteps = walkOnce(rng, pageGraph, entryPath, 5 + rng.int(5));
      if (!pathSteps.includes("/appointments")) pathSteps.push("/appointments");
      if (!pathSteps.includes("/appointments/new")) {
        pathSteps.push("/appointments/new");
      }
    }

    const events: PreviewSessionEvent[] = [];
    let cursor = startedAt;
    const perPageMs = 15_000 + rng.int(45_000); // 15s–60s between pages

    pathSteps.forEach((step, idx) => {
      events.push({ eventName: "page_view", path: step, timestamp: cursor });
      const traversedEdge =
        idx > 0
          ? pageGraph.edges.find(
              (e) => e.from === pathSteps[idx - 1] && e.to === step,
            )
          : undefined;
      if (traversedEdge?.events) {
        for (const name of traversedEdge.events) {
          events.push({ eventName: name, path: step, timestamp: cursor + 500 });
        }
      }
      cursor += perPageMs;
    });

    if (outcome === "engaged" && rng.next() < 0.3) {
      events.push({
        eventName: "form_started",
        path: pathSteps[pathSteps.length - 1],
        timestamp: cursor,
      });
      cursor += 5_000;
    }

    if (outcome === "converting") {
      events.push({
        eventName: "form_started",
        path: "/appointments/new",
        timestamp: cursor,
      });
      cursor += 8_000;
      events.push({
        eventName: "form_submitted",
        path: "/appointments/new",
        timestamp: cursor,
      });
      cursor += 2_000;
      // 85% booking completion (followed by confirmation page), 15% phone-call fallback
      if (rng.next() < 0.85) {
        events.push({
          eventName: "appointment_booking_completed",
          path: "/appointments/new",
          timestamp: cursor,
        });
        cursor += 3_000;
        events.push({
          eventName: "page_view",
          path: "/appointments/new/confirmation",
          timestamp: cursor,
        });
      } else {
        events.push({
          eventName: "call_initiated",
          path: pathSteps[pathSteps.length - 2] ?? "/appointments",
          timestamp: cursor,
        });
      }
    }

    sessions.push({
      sessionID: rng.uuid(),
      deviceID: device.deviceID,
      channel,
      utm: utmFor(channel, rng),
      outcome,
      startedAt,
      events,
    });
  }

  return sessions;
}
